// src/components/screener/FilterPanel.jsx
// SCREENER-FRAG — new file. Renders SCREENER_FIELD_REGISTRY as the tabbed
// Descriptive/Fundamental/Technical/News/ETF/All layout from the owner's
// reference screenshot. This is the piece that makes a future field a data
// row insert: add an entry to screenerFieldRegistry.js and it appears here
// automatically, in the right tab, with the right control type — no edits
// to this file required.
//
// Ready fields render as live controls and call onChange(key, value).
// Stub fields render muted with a "pipeline pending" tag and are disabled —
// deliberately not just visually muted while silently accepting input. A
// control that looks interactive but no-ops on change is the same trust
// problem as a silent data failure, just moved to the member-facing surface.
//
// excludeKeys (optional prop, default []): field keys to omit entirely.
// Used for 'sector' — that filter already has a dedicated, pre-existing
// dropdown in the top filter bar (signalFilter/sectorFilter/sortBy), so this
// panel does not render a second, competing control for the same value.
// Industry/Country/edgarFilingTier are genuinely new, not duplicates.
//
// fieldOptions (optional prop): { [key]: string[] } — overrides a registry
// field's static `options` for select-type ready fields whose true option
// list is a distinct-values query (industry, country) rather than a fixed
// enum. NOT wired to a live endpoint yet — GET /api/screener/filter-options
// doesn't exist. Until it does, industry/country render as a functional but
// empty dropdown — a real follow-up, not silently treated as finished.
//
// Props:
//   activeValues  { [key]: value } — current filter values, keyed by field key
//   onChange      (key, value) => void
//   excludeKeys   string[] — field keys to omit (default [])
//   fieldOptions  { [key]: string[] } — see above

import React, { useState } from 'react';
import { S } from '../../constants/screenerConfig';
import { SCREENER_CATEGORIES, getFieldsByCategory } from '../../constants/screenerFieldRegistry';

const TAB_LABELS = {
  descriptive: 'Descriptive',
  fundamental: 'Fundamental',
  technical:   'Technical',
  news:        'News',
  etf:         'ETF',
  government:  'Gov & Institutional', // the differentiator — not a Finviz/TradingView category
  all:         'All',
};

// SCREENER-FINANCIALS — unit conversion for percent fields. Real gap found
// this arc: xbrl_financials_computed stores ROE/ROA/margins/growth as
// decimal fractions (0.15 = 15%), but this panel's range inputs are meant
// to match Finviz's own convention (member types "15" meaning 15%) per this
// file's own header. Without this, a member typing "15" for Min ROE would
// be silently compared against a value 100x too small server-side and every
// real company would fail the filter. Converts only at the input boundary —
// activeValues (owned by the parent, sent straight to the API) always holds
// the wire-format decimal fraction; the box always displays/accepts the
// whole-number percent. Round-trips cleanly because onChange always carries
// the full current input string, not an incremental keystroke.
function toDecimalFraction(percentStr) {
  if (percentStr === '' || percentStr == null) return '';
  const n = Number(percentStr);
  return Number.isFinite(n) ? String(n / 100) : '';
}
function toPercentDisplay(fractionVal) {
  if (fractionVal === '' || fractionVal == null) return '';
  const n = Number(fractionVal);
  return Number.isFinite(n) ? String(n * 100) : '';
}

function FieldControl({ field, value, onChange, options }) {
  const isStub = field.status === 'stub';
  const opts   = options && options.length ? options : (field.options || []);

  const baseStyle = {
    width: '100%', padding: '6px 10px', borderRadius: 6, fontSize: 12,
    background: isStub ? S.bg : S.surface,
    color: isStub ? S.subtle : S.text,
    border: `1px solid ${isStub ? S.border : S.border2}`,
    fontFamily: 'inherit', cursor: isStub ? 'not-allowed' : 'pointer',
  };

  if (field.type === 'select') {
    return (
      <select
        disabled={isStub}
        value={value ?? 'Any'}
        onChange={e => onChange(field.key, e.target.value)}
        style={baseStyle}
      >
        <option value="Any">Any</option>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  if (field.type === 'range') {
    const isPercent = field.unit === 'percent';
    const displayMin = isPercent ? toPercentDisplay(value?.min) : (value?.min ?? '');
    const displayMax = isPercent ? toPercentDisplay(value?.max) : (value?.max ?? '');
    const emitMin = (raw) => isPercent ? toDecimalFraction(raw) : raw;
    const emitMax = (raw) => isPercent ? toDecimalFraction(raw) : raw;

    // CHECKBOX-GATED RANGE (this session, owner-directed UX): for a field like
    // "Amended Filing (Year)" a bare blank Min/Max box isn't helpful — a member
    // has no cue what a reasonable window even is. gatedByCheckbox fields
    // instead show a checkbox first; checking it POPULATES activeValues with
    // the field's own defaultRangeYearsBack window (computed here, from the
    // real current year, not hardcoded at registry-definition time) and THEN
    // reveals editable Min/Max pre-filled with that default — a member who
    // wants a different window edits from there. Unchecking clears the value
    // entirely (same as never having set a filter). No parent/ScreenerPage.jsx
    // change needed: "checked" is derived from whether activeValues already
    // holds a value for this field, not separate local state — one fewer
    // thing that can drift out of sync with the actual filter being applied.
    if (field.gatedByCheckbox) {
      const isChecked = value != null;
      const currentYear = new Date().getFullYear();
      const defaultRange = {
        min: String(currentYear - (field.defaultRangeYearsBack ?? 5)),
        max: String(currentYear),
      };
      return (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: isChecked ? 6 : 0, cursor: isStub ? 'not-allowed' : 'pointer' }}>
            <input
              type="checkbox" disabled={isStub}
              checked={isChecked}
              onChange={e => onChange(field.key, e.target.checked ? defaultRange : undefined)}
              style={{ accentColor: S.purple, cursor: isStub ? 'not-allowed' : 'pointer' }}
            />
            <span style={{ fontSize: 11, color: isStub ? S.subtle : S.text }}>
              {field.gatedCheckboxLabel || 'Enable'}
            </span>
          </label>
          {isChecked && (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="number" placeholder="Min" disabled={isStub}
                value={value?.min ?? ''}
                onChange={e => onChange(field.key, { ...value, min: e.target.value })}
                style={{ ...baseStyle, width: '50%' }}
              />
              <input
                type="number" placeholder="Max" disabled={isStub}
                value={value?.max ?? ''}
                onChange={e => onChange(field.key, { ...value, max: e.target.value })}
                style={{ ...baseStyle, width: '50%' }}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input
          type="number" placeholder="Min" disabled={isStub}
          value={displayMin}
          onChange={e => onChange(field.key, { ...value, min: emitMin(e.target.value) })}
          style={{ ...baseStyle, width: '50%' }}
        />
        <input
          type="number" placeholder="Max" disabled={isStub}
          value={displayMax}
          onChange={e => onChange(field.key, { ...value, max: emitMax(e.target.value) })}
          style={{ ...baseStyle, width: '50%' }}
        />
        {isPercent && <span style={{ fontSize: 11, color: S.muted, flexShrink: 0 }}>%</span>}
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <input
        type="checkbox" disabled={isStub}
        checked={!!value}
        onChange={e => onChange(field.key, e.target.checked)}
        style={{ accentColor: S.purple, cursor: isStub ? 'not-allowed' : 'pointer' }}
      />
    );
  }

  // text
  return (
    <input
      type="text" disabled={isStub} placeholder="Any"
      value={value ?? ''}
      onChange={e => onChange(field.key, e.target.value)}
      style={baseStyle}
    />
  );
}

export default function FilterPanel({ activeValues = {}, onChange, excludeKeys = [], fieldOptions = {} }) {
  const [activeTab, setActiveTab] = useState('all');
  const fields = getFieldsByCategory(activeTab).filter(f => !excludeKeys.includes(f.key));

  return (
    <div style={{
      background: S.surface, border: `1px solid ${S.border2}`, borderRadius: 10,
      marginBottom: 16, overflow: 'hidden',
    }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${S.border2}`, padding: '4px 8px', gap: 4 }}>
        {[...SCREENER_CATEGORIES, 'all'].map(cat => (
          <button
            key={cat}
            onClick={() => setActiveTab(cat)}
            style={{
              padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700,
              background: activeTab === cat ? 'rgba(99,102,241,0.15)' : 'transparent',
              color: activeTab === cat ? S.purple : S.muted,
              border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {TAB_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Field grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '10px 16px', padding: '14px 16px',
      }}>
        {fields.map(field => (
          <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{
              fontSize: 11, color: field.status === 'stub' ? S.subtle : S.muted,
              display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600,
            }}>
              {field.label}
              {field.status === 'stub' && (
                <span style={{
                  fontSize: 9, padding: '1px 6px', borderRadius: 8,
                  background: 'rgba(139,139,167,0.15)', color: S.subtle, fontWeight: 700,
                }}>
                  PIPELINE PENDING
                </span>
              )}
            </label>
            <FieldControl
              field={field}
              value={activeValues[field.key]}
              onChange={onChange}
              options={fieldOptions[field.key]}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
